import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  ThemeProvider, 
  createTheme, 
  CssBaseline,
  alpha 
} from "@mui/material";
import * as Sentry from "@sentry/react";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedbackIcon from '@mui/icons-material/Feedback';

export class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    // Helper to get theme tokens without using hooks
    getTheme = () => {
        const stored = localStorage.getItem('themeMode');
        const isDark = stored === 'dark' || 
            (!stored && window.matchMedia?.('(prefers-color-scheme: dark)').matches);

        const palette = isDark ? {
            mode: 'dark',
            background: { default: '#0a0a0a', paper: '#1a1a1a' },
            primary: { main: '#ffffff', contrastText: '#000000' },
            text: { primary: '#ffffff', secondary: '#cccccc' },
            error: { main: '#f44336' }
        } : {
            mode: 'light',
            background: { default: '#ffffff', paper: '#f8fafc' },
            primary: { main: '#000000', contrastText: '#ffffff' },
            text: { primary: '#1f2937', secondary: '#6b7280' },
            error: { main: '#d32f2f' }
        };

        return createTheme({
            palette,
            components: {
                MuiButton: {
                    styleOverrides: {
                        root: { textTransform: 'none', borderRadius: 6 },
                        containedPrimary: {
                            backgroundColor: palette.primary.main,
                            color: palette.primary.contrastText,
                            '&:hover': { backgroundColor: isDark ? '#f1f5f9' : '#1f2937' },
                        },
                        outlinedSecondary: {
                            color: palette.text.secondary,
                            borderColor: isDark ? '#333333' : '#d1d5db',
                            '&:hover': { 
                                borderColor: palette.primary.main,
                                backgroundColor: alpha(palette.primary.main, 0.04) 
                            },
                        }
                    }
                }
            }
        });
    }

    handleRefresh = () => window.location.reload();

    handleReportIssue = () => {
        const eventId = this.props.eventId || Sentry.lastEventId();
        Sentry.showReportDialog({ eventId });
    };

    render() {
        if (this.props.error || this.state.hasError) {
            const theme = this.getTheme();

            return (
                <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        minHeight={this.props.fullPage ? "100vh" : "400px"}
                        width="100%"
                        textAlign="center"
                        bgcolor="background.default"
                        color="text.primary"
                        sx={{ py: 4, px: 2 }}
                    >
                        <ErrorOutlineIcon 
                            sx={{ fontSize: 64, color: "error.main", mb: 2 }} 
                        />
                        
                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                            Something went wrong
                        </Typography>
                        
                        <Typography variant="body1" sx={{ mb: 1, maxWidth: 500 }}>
                            An unexpected error occurred in the application.
                        </Typography>
                        
                        <Typography color="text.secondary" sx={{ mb: 4, maxWidth: 450 }}>
                            Our team has been notified. You can try refreshing the page or report the issue to help us fix it.
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Button 
                                variant="contained" 
                                onClick={this.handleRefresh}
                                startIcon={<RefreshIcon />}
                                size="large"
                            >
                                Refresh Page
                            </Button>

                            <Button 
                                variant="outlined" 
                                color="secondary"
                                onClick={this.handleReportIssue}
                                startIcon={<FeedbackIcon />}
                                size="large"
                            >
                                Report Issue
                            </Button>
                        </Box>
                    </Box>
                </ThemeProvider>
            );
        }

        return this.props.children;
    }
}